import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import { trainingRequest } from './api';
import { COPY } from './copy';
import { useOwnerQa } from '../contexts/OwnerQaContext';
import './training-payments.css';

const PaidTrainingContext = createContext({ session: null, simulatedSession: null, isPro: false });
export const usePaidTraining = () => useContext(PaidTrainingContext);

export default function TrainingAccess({ kind = 'puzzle', children }) {
  const { firebaseUser, loading: authLoading } = useContext(UserContext);
  const { language } = useLanguage();
  const qa = useOwnerQa();
  const text = COPY[language] || COPY.en;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selected = params.get('session') || '';
  const qaSelected = params.get('qaSession') || '';
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setResult(null); setError(false);
    if (!firebaseUser) return;
    // QA simulated passes and subscriptions need no production billing API.
    if (qa.active && (qaSelected || (!selected && qa.config.subscriptionMode !== 'real'))) return;
    const controller = new AbortController();
    trainingRequest('sessions', { signal: controller.signal }).then(data => setResult({ ...data, loadedAt: Date.now(), uid: firebaseUser.uid })).catch(e => {
      if (e.name !== 'AbortError') setError(true);
    });
    return () => controller.abort();
  }, [firebaseUser, selected, qaSelected, qa.active, qa.config.subscriptionMode]);
  useEffect(() => { const tick = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(tick); }, []);

  if (authLoading) return <div className="training-gate">{text.checking}</div>;
  if (!firebaseUser) return <Navigate to="/training" replace />;

  const allows = (s) => kind === 'hood' ? ['hood', 'lichess'].includes(s.mode) : Boolean(s.mode);

  if (qa.active && qaSelected) {
    const simulatedSession = qa.getPass(qaSelected);
    if (!simulatedSession || !allows(simulatedSession) || !['active', 'expired'].includes(simulatedSession.state)) {
      return <Navigate to="/training" replace />;
    }
    if (simulatedSession.state !== 'active' || simulatedSession.secondsRemaining <= 0) {
      return <div className="training-gate"><h1>{text.ended}</h1><p>QA simulated pass expired.</p><Link to="/training">{text.back} →</Link></div>;
    }
    return <PaidTrainingContext.Provider value={{ session: null, simulatedSession, isPro: false }}>
      <div className="training-session-bar"><Link to="/training">← {text.yours}</Link><strong>QA · {text.modes[simulatedSession.mode]?.[0] || simulatedSession.mode}</strong><span>{text.remaining} {Math.floor(simulatedSession.secondsRemaining / 60)}:{String(simulatedSession.secondsRemaining % 60).padStart(2, '0')}</span></div>
      {children}
    </PaidTrainingContext.Provider>;
  }

  if (qa.active && !selected && qa.config.subscriptionMode !== 'real') {
    const simulatedUnlimited =
      qa.config.subscriptionMode === 'pro' ||
      (qa.config.subscriptionMode === 'trial' && qa.hoodAccess?.phase === 'trial');
    if (simulatedUnlimited) {
      return <PaidTrainingContext.Provider value={{ session: null, simulatedSession: null, isPro: true }}>{children}</PaidTrainingContext.Provider>;
    }
    return <Navigate to="/training" replace />;
  }

  if (error) return <div className="training-gate" role="alert">{text.error}<p><Link to="/training">{text.back}</Link></p></div>;
  if (!result || result.uid !== firebaseUser.uid) return <div className="training-gate" role="status">{text.checking}</div>;
  const session = selected ? result.sessions.find(s => s.id === selected && allows(s)) : result.sessions.find(s => s.state === 'active' && allows(s));
  if (!selected && session) {
    const params = new URLSearchParams(location.search); params.set('session', session.id);
    return <Navigate to={`${location.pathname}?${params}`} replace />;
  }
  if (!selected && result.isPro) return <PaidTrainingContext.Provider value={{ session: null, simulatedSession: null, isPro: true }}>{children}</PaidTrainingContext.Provider>;
  if (!session || session.state === 'ready') return <Navigate to="/training" replace />;
  const seconds = Math.max(0, (session.secondsRemaining || 0) - Math.max(0, Math.floor((now - result.loadedAt) / 1000)));
  if (session.state !== 'active' || seconds <= 0) return <div className="training-gate"><h1>{text.ended}</h1><Link to="/training">{text.back} →</Link></div>;
  return <PaidTrainingContext.Provider value={{ session, simulatedSession: null, isPro: result.isPro }}>
    <div className="training-session-bar"><Link to="/training">← {text.yours}</Link><strong>{text.modes[session.mode]?.[0]}</strong><span>{text.remaining} {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</span></div>
    {children}
  </PaidTrainingContext.Provider>;
}
